import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, FileText, TrendingUp, TrendingDown, DollarSign, Percent, PieChart, BarChart3, Activity, ArrowLeft, ArrowUpDown, ArrowUp, ArrowDown, Target, ShieldAlert, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { PieChart as RechartsPieChart, BarChart, XAxis, YAxis, CartesianGrid, Cell, Pie, Tooltip, Legend, ResponsiveContainer, Bar, LineChart, Line } from "recharts";
import { useNavigate } from 'react-router-dom';
import { IndividualStockScorecard } from '@/components/IndividualStockScorecard';
import { cn } from "@/lib/utils";

interface AccountSummary {
  account: string;
  name: string;
  beginningNAV: number;
  endingNAV: number;
  return: number;
  deposits: number;
  withdrawals: number;
  dividends: number;
  interest: number;
  fees: number;
}

interface KeyStatistics {
  beginningNAV: number;
  endingNAV: number;
  cumulativeReturn: number;
  fiveDayReturn: number;
  tenDayReturn: number;
  bestReturn: number;
  bestReturnDate: string;
  worstReturn: number;
  worstReturnDate: string;
  mtm: number;
  depositsWithdrawals: number;
  dividends: number;
  interest: number;
  feesCommissions: number;
  changeInNAV: number;
}

interface Position {
  ticker: string;
  description: string;
  sector: string;
  quantity: number;
  marketValue: number;
  costBasis: number;
  unrealizedPL: number;
  unrealizedPLPercent: number;
  currentPrice: number;
  avgCost: number;
  currency: string;
}

interface BenchmarkComparison {
  period: string;
  portfolio: number | null;
  sp500: number | null;
  efa: number | null;
  vt: number | null;
}

interface BenchmarkSummary {
  name: string;
  mtd: number | null;
  qtd: number | null;
  ytd: number | null;
  oneYear: number | null;
  sinceInception: number | null;
}

interface PositionsByCategory {
  category: string;
  positions: Position[];
  totalValue: number;
  totalCostBasis: number;
  totalUnrealizedPL: number;
  totalUnrealizedPLPercent: number;
}

interface AnalysisData {
  accountSummary: AccountSummary[];
  keyStatistics: KeyStatistics;
  positionsByCategory: PositionsByCategory[];
  allPositions: Position[];
  monthlyComparison: BenchmarkComparison[];
  quarterlyComparison: BenchmarkComparison[];
  benchmarkSummary: BenchmarkSummary[];
  totalValue: number;
  totalCostBasis: number;
  totalUnrealizedPL: number;
  totalUnrealizedPLPercent: number;
}

type SortField = keyof Position;
type SortDirection = 'asc' | 'desc' | null;

const IBKRImport = () => {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [analysisData, setAnalysisData] = useState<AnalysisData | null>(null);
  const [sortField, setSortField] = useState<SortField>('marketValue');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedPosition, setSelectedPosition] = useState<Position | null>(null);
  const [scorecardOpen, setScorecardOpen] = useState(false);
  const { toast } = useToast();

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : sortDirection === 'desc' ? null : 'asc');
      if (sortDirection === 'desc') {
        setSortField('marketValue');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedPositions = (positions: Position[]) => {
    if (!sortDirection) {
      return [...positions].sort((a, b) => b.marketValue - a.marketValue);
    }

    return [...positions].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      return sortDirection === 'asc' 
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 ml-1 inline opacity-30" />;
    if (sortDirection === 'asc') return <ArrowUp className="w-4 h-4 ml-1 inline" />;
    if (sortDirection === 'desc') return <ArrowDown className="w-4 h-4 ml-1 inline" />;
    return <ArrowUpDown className="w-4 h-4 ml-1 inline opacity-30" />;
  };

  const parseCSV = (csvText: string): AnalysisData => {
    const lines = csvText.split('\n');
    const accountSummary: AccountSummary[] = [];
    const positions: Position[] = [];
    const positionsMap = new Map<string, Position[]>();
    const monthlyData: BenchmarkComparison[] = [];
    const quarterlyData: BenchmarkComparison[] = [];
    const benchmarkSummaryData: BenchmarkSummary[] = [];
    let keyStats: KeyStatistics | null = null;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const parts = line.split(',');
      
      if (parts[0] === 'Breakdown of Accounts' && parts[1] === 'Data') {
        accountSummary.push({
          account: parts[2],
          name: parts[3],
          beginningNAV: parseFloat(parts[4]) || 0,
          endingNAV: parseFloat(parts[5]) || 0,
          return: parseFloat(parts[6]) || 0,
          deposits: parseFloat(parts[7]) || 0,
          withdrawals: parseFloat(parts[8]) || 0,
          dividends: parseFloat(parts[9]) || 0,
          interest: parseFloat(parts[10]) || 0,
          fees: parseFloat(parts[11]) || 0,
        });
      }

      if (parts[0] === 'Key Statistics' && parts[1] === 'Data') {
        keyStats = {
          beginningNAV: parseFloat(parts[2]) || 0,
          endingNAV: parseFloat(parts[3]) || 0,
          cumulativeReturn: parseFloat(parts[4]) || 0,
          fiveDayReturn: parseFloat(parts[5]) || 0,
          tenDayReturn: parseFloat(parts[7]) || 0,
          bestReturn: parseFloat(parts[9]) || 0,
          bestReturnDate: parts[10] || '',
          worstReturn: parseFloat(parts[11]) || 0,
          worstReturnDate: parts[12] || '',
          mtm: parseFloat(parts[13]) || 0,
          depositsWithdrawals: parseFloat(parts[14]) || 0,
          dividends: parseFloat(parts[15]) || 0,
          interest: parseFloat(parts[16]) || 0,
          feesCommissions: parseFloat(parts[17]) || 0,
          changeInNAV: parseFloat(parts[19]) || 0,
        };
      }

      if (parts[0] === 'Open Position Summary' && parts[1] === 'Data' && parts[3] !== 'Total') {
        const category = parts[3];
        const currency = parts[4]?.trim() || 'USD'; // Currency is in column 4
        const ticker = parts[5] || '';
        const description = parts[6] || '';
        const quantity = parseFloat(parts[8]) || 0;
        const marketValue = parseFloat(parts[10]) || 0;
        const costBasis = parseFloat(parts[11]) || 0;
        const unrealizedPL = parseFloat(parts[12]) || 0;

        if (ticker && category !== 'Cash') {
          const position: Position = {
            ticker,
            description,
            sector: parts[7] || '',
            quantity,
            marketValue,
            costBasis,
            unrealizedPL,
            unrealizedPLPercent: costBasis !== 0 ? (unrealizedPL / Math.abs(costBasis)) * 100 : 0,
            currentPrice: quantity !== 0 ? marketValue / quantity : 0,
            avgCost: quantity !== 0 ? costBasis / quantity : 0,
            currency,
          };

          if (!positionsMap.has(category)) {
            positionsMap.set(category, []);
          }
          positionsMap.get(category)!.push(position);
          positions.push(position);
        }
      }

      // Parse benchmark summary (MTD, QTD, YTD, etc.)
      if (parts[0] === 'Historical Performance Benchmark Comparison' && parts[1] === 'Data') {
        const period = parts[2];
        
        // Benchmark summary rows
        if (period && !period.match(/^\d{4}/) && !period.includes('Q') && parts[3]) {
          const benchmarkName = period === 'Consolidated' ? 'Portfolio' : period;
          benchmarkSummaryData.push({
            name: benchmarkName,
            mtd: parts[3] ? parseFloat(parts[3]) : null,
            qtd: parts[4] ? parseFloat(parts[4]) : null,
            ytd: parts[5] ? parseFloat(parts[5]) : null,
            oneYear: parts[6] ? parseFloat(parts[6]) : null,
            sinceInception: parts[7] ? parseFloat(parts[7]) : null,
          });
        }
        
        // Monthly performance (both 2024 and 2025)
        if (period && period.match(/^\d{6}$/) && parts[10]) {
          monthlyData.push({
            period,
            portfolio: parts[10] ? parseFloat(parts[10]) : null,
            sp500: parts[4] ? parseFloat(parts[4]) : null,
            efa: parts[6] ? parseFloat(parts[6]) : null,
            vt: parts[8] ? parseFloat(parts[8]) : null,
          });
        }
        
        // Quarterly performance
        if (period && period.includes('Q') && parts[10]) {
          quarterlyData.push({
            period,
            portfolio: parts[10] ? parseFloat(parts[10]) : null,
            sp500: parts[4] ? parseFloat(parts[4]) : null,
            efa: parts[6] ? parseFloat(parts[6]) : null,
            vt: parts[8] ? parseFloat(parts[8]) : null,
          });
        }
      }
    }

    const positionsByCategory: PositionsByCategory[] = Array.from(positionsMap.entries()).map(([category, categoryPositions]) => {
      const totalValue = categoryPositions.reduce((sum, p) => sum + p.marketValue, 0);
      const totalCostBasis = categoryPositions.reduce((sum, p) => sum + p.costBasis, 0);
      const totalUnrealizedPL = categoryPositions.reduce((sum, p) => sum + p.unrealizedPL, 0);
      
      return {
        category,
        positions: categoryPositions,
        totalValue,
        totalCostBasis,
        totalUnrealizedPL,
        totalUnrealizedPLPercent: totalCostBasis !== 0 ? (totalUnrealizedPL / Math.abs(totalCostBasis)) * 100 : 0,
      };
    });

    const totalValue = positions.reduce((sum, p) => sum + p.marketValue, 0);
    const totalCostBasis = positions.reduce((sum, p) => sum + p.costBasis, 0);
    const totalUnrealizedPL = positions.reduce((sum, p) => sum + p.unrealizedPL, 0);

    return {
      accountSummary,
      keyStatistics: keyStats || {
        beginningNAV: 0,
        endingNAV: 0,
        cumulativeReturn: 0,
        fiveDayReturn: 0,
        tenDayReturn: 0,
        bestReturn: 0,
        bestReturnDate: '',
        worstReturn: 0,
        worstReturnDate: '',
        mtm: 0,
        depositsWithdrawals: 0,
        dividends: 0,
        interest: 0,
        feesCommissions: 0,
        changeInNAV: 0,
      },
      positionsByCategory,
      allPositions: positions,
      monthlyComparison: monthlyData,
      quarterlyComparison: quarterlyData,
      benchmarkSummary: benchmarkSummaryData,
      totalValue,
      totalCostBasis,
      totalUnrealizedPL,
      totalUnrealizedPLPercent: totalCostBasis !== 0 ? (totalUnrealizedPL / Math.abs(totalCostBasis)) * 100 : 0,
    };
  };

  const handleFileUpload = async (file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast({
        title: "Invalid file type",
        description: "Please upload a CSV file",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    try {
      const text = await file.text();
      const data = parseCSV(text);
      setAnalysisData(data);
      
      toast({
        title: "Success",
        description: `Parsed ${data.allPositions.length} positions across ${data.positionsByCategory.length} categories`,
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "Failed to process CSV file",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B9D', '#8DD1E1', '#D084D0'];
  
  const getCurrencySymbol = (currency: string | undefined): string => {
    if (!currency || typeof currency !== 'string') return '?';
    
    const symbols: Record<string, string> = {
      'USD': '$',
      'EUR': '€',
      'CHF': 'CHF',
      'GBP': '£',
      'JPY': '¥',
      'CAD': 'C$',
      'AUD': 'A$',
      'NZD': 'NZ$',
      'HKD': 'HK$',
      'SGD': 'S$',
      'SEK': 'kr',
      'NOK': 'kr',
      'DKK': 'kr',
    };
    
    const upperCurrency = currency.toUpperCase().trim();
    return symbols[upperCurrency] || upperCurrency;
  };
  
  const formatCurrency = (value: number, currency: string = 'USD') => {
    const symbol = getCurrencySymbol(currency);
    return `${symbol}${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  
  const formatPercent = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-background p-4 md:p-8">
      <div className="max-w-[1600px] mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/')}
            className="hover:bg-muted"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="text-center flex-1">
            <h1 className="text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary via-purple-600 to-pink-600">
              IBKR Portfolio Analytics
            </h1>
            <p className="text-lg text-muted-foreground mt-2">
              Upload your Interactive Brokers CSV statement for comprehensive portfolio analysis
            </p>
          </div>
        </div>

        {!analysisData && (
          <Card className="border-2 border-dashed border-primary/20 hover:border-primary/40 transition-all hover:shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Upload CSV Statement
              </CardTitle>
              <CardDescription>
                Upload your Interactive Brokers CSV report (Activity Statement) for comprehensive analysis
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isProcessing ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-center p-8">
                    <div className="text-center space-y-4">
                      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto"></div>
                      <p className="text-muted-foreground">Processing CSV file...</p>
                    </div>
                  </div>
                  <Progress value={66} className="w-full" />
                </div>
              ) : (
                <div
                  className="border-2 border-dashed border-border rounded-lg p-12 text-center hover:border-primary/50 transition-colors cursor-pointer"
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    if (file) handleFileUpload(file);
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onClick={() => document.getElementById('file-upload')?.click()}
                >
                  <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-lg font-medium mb-2">Drop your CSV file here</p>
                  <p className="text-sm text-muted-foreground mb-4">or click to browse</p>
                  <input
                    id="file-upload"
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file);
                    }}
                  />
                  <Button variant="outline">
                    <Upload className="w-4 h-4 mr-2" />
                    Select CSV File
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {analysisData && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold">Portfolio Analysis</h2>
              <Button onClick={() => setAnalysisData(null)} variant="outline">
                Upload New File
              </Button>
            </div>

            {/* Key Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Portfolio Value</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{formatCurrency(analysisData.totalValue, 'USD')}</div>
                  <p className="text-sm text-muted-foreground mt-1">Ending NAV: {formatCurrency(analysisData.keyStatistics.endingNAV, 'USD')}</p>
                </CardContent>
              </Card>

              <Card className={analysisData.totalUnrealizedPL >= 0 ? "bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/20" : "bg-gradient-to-br from-red-500/10 to-red-500/5 border-red-500/20"}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Unrealized P/L</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`text-3xl font-bold ${analysisData.totalUnrealizedPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(analysisData.totalUnrealizedPL, 'USD')}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {analysisData.totalUnrealizedPL >= 0 ? <TrendingUp className="w-4 h-4 text-green-600" /> : <TrendingDown className="w-4 h-4 text-red-600" />}
                    <span className={`text-sm font-medium ${analysisData.totalUnrealizedPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatPercent(analysisData.totalUnrealizedPLPercent)}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card className={analysisData.keyStatistics.cumulativeReturn >= 0 ? "bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20" : "bg-gradient-to-br from-orange-500/10 to-orange-500/5 border-orange-500/20"}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground">YTD Return</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`text-3xl font-bold ${analysisData.keyStatistics.cumulativeReturn >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                    {formatPercent(analysisData.keyStatistics.cumulativeReturn)}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Change: {formatCurrency(analysisData.keyStatistics.changeInNAV, 'USD')}</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 border-purple-500/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Cost Basis</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{formatCurrency(analysisData.totalCostBasis, 'USD')}</div>
                  <p className="text-sm text-muted-foreground mt-1">{analysisData.allPositions.length} positions</p>
                </CardContent>
              </Card>
            </div>

            {/* Performance Metrics */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Performance Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">5-Day Return</p>
                    <p className={`text-lg font-bold ${analysisData.keyStatistics.fiveDayReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatPercent(analysisData.keyStatistics.fiveDayReturn)}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">10-Day Return</p>
                    <p className={`text-lg font-bold ${analysisData.keyStatistics.tenDayReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatPercent(analysisData.keyStatistics.tenDayReturn)}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Best Day</p>
                    <p className="text-lg font-bold text-green-600">{formatPercent(analysisData.keyStatistics.bestReturn)}</p>
                    <p className="text-xs text-muted-foreground">{analysisData.keyStatistics.bestReturnDate}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Worst Day</p>
                    <p className="text-lg font-bold text-red-600">{formatPercent(analysisData.keyStatistics.worstReturn)}</p>
                    <p className="text-xs text-muted-foreground">{analysisData.keyStatistics.worstReturnDate}</p>
                  </div>
                </div>

                 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Dividends</p>
                    <p className="text-lg font-bold">{formatCurrency(analysisData.keyStatistics.dividends, 'USD')}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Interest</p>
                    <p className="text-lg font-bold">{formatCurrency(analysisData.keyStatistics.interest, 'USD')}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Fees</p>
                    <p className="text-lg font-bold text-red-600">{formatCurrency(analysisData.keyStatistics.feesCommissions, 'USD')}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Net Deposits</p>
                    <p className="text-lg font-bold">{formatCurrency(analysisData.keyStatistics.depositsWithdrawals, 'USD')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Account Summary - Show if multiple accounts */}
            {analysisData.accountSummary.length > 1 && (
              <Card>
                <CardHeader>
                  <CardTitle>Account Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2">Account</th>
                          <th className="text-left p-2">Name</th>
                          <th className="text-right p-2">Beginning NAV</th>
                          <th className="text-right p-2">Ending NAV</th>
                          <th className="text-right p-2">Return %</th>
                          <th className="text-right p-2">Deposits</th>
                          <th className="text-right p-2">Withdrawals</th>
                          <th className="text-right p-2">Dividends</th>
                          <th className="text-right p-2">Interest</th>
                          <th className="text-right p-2">Fees</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analysisData.accountSummary.map((acc, idx) => (
                          <tr key={idx} className="border-b hover:bg-muted/50">
                            <td className="p-2 font-medium">{acc.account}</td>
                            <td className="p-2">{acc.name}</td>
                            <td className="text-right p-2">{formatCurrency(acc.beginningNAV, 'USD')}</td>
                            <td className="text-right p-2 font-medium">{formatCurrency(acc.endingNAV, 'USD')}</td>
                            <td className={`text-right p-2 font-medium ${acc.return >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatPercent(acc.return)}
                            </td>
                            <td className="text-right p-2">{formatCurrency(acc.deposits, 'USD')}</td>
                            <td className="text-right p-2">{formatCurrency(acc.withdrawals, 'USD')}</td>
                            <td className="text-right p-2 text-green-600">{formatCurrency(acc.dividends, 'USD')}</td>
                            <td className="text-right p-2 text-green-600">{formatCurrency(acc.interest, 'USD')}</td>
                            <td className="text-right p-2 text-red-600">{formatCurrency(acc.fees, 'USD')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            <Tabs defaultValue="overview" className="space-y-4">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="categories">By Category</TabsTrigger>
                <TabsTrigger value="positions">All Positions</TabsTrigger>
                <TabsTrigger value="benchmarks">Benchmarks</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Portfolio Allocation Pie Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <PieChart className="w-5 h-5" />
                        Portfolio Allocation
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <RechartsPieChart>
                          <Pie
                            data={analysisData.positionsByCategory.map(cat => ({
                              name: cat.category,
                              value: cat.totalValue
                            }))}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {analysisData.positionsByCategory.map((_, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => formatCurrency(value as number, 'USD')} />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Top Gainers & Losers */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5" />
                        Top 10 Gainers & Losers
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={[...analysisData.allPositions]
                            .sort((a, b) => Math.abs(b.unrealizedPL) - Math.abs(a.unrealizedPL))
                            .slice(0, 10)
                            .map(pos => ({
                              ticker: pos.ticker,
                              pl: pos.unrealizedPL,
                              plPercent: pos.unrealizedPLPercent
                            }))}
                          layout="vertical"
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis dataKey="ticker" type="category" width={80} />
                          <Tooltip 
                            formatter={(value, name) => [
                              name === 'pl' ? formatCurrency(value as number) : formatPercent(value as number),
                              name === 'pl' ? 'P/L' : 'P/L %'
                            ]}
                          />
                          <Bar dataKey="pl" fill="#8884d8">
                            {[...analysisData.allPositions]
                              .sort((a, b) => Math.abs(b.unrealizedPL) - Math.abs(a.unrealizedPL))
                              .slice(0, 10)
                              .map((pos, index) => (
                                <Cell key={`cell-${index}`} fill={pos.unrealizedPL >= 0 ? '#22c55e' : '#ef4444'} />
                              ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="categories" className="space-y-4">
                {analysisData.positionsByCategory.map((category, idx) => (
                  <Card key={idx}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                          {category.category}
                          <Badge variant="secondary">{category.positions.length} positions</Badge>
                        </CardTitle>
                        <div className="text-right">
                          <p className="text-2xl font-bold">{formatCurrency(category.totalValue, 'USD')}</p>
                          <p className={`text-sm font-medium ${category.totalUnrealizedPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(category.totalUnrealizedPL, 'USD')} ({formatPercent(category.totalUnrealizedPLPercent)})
                          </p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left p-2">Ticker</th>
                              <th className="text-left p-2">Description</th>
                              <th className="text-right p-2">Quantity</th>
                              <th className="text-right p-2">Market Value</th>
                              <th className="text-right p-2">Cost Basis</th>
                              <th className="text-right p-2">Unrealized P/L</th>
                              <th className="text-right p-2">P/L %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {category.positions.map((pos, posIdx) => (
                              <tr key={posIdx} className="border-b hover:bg-muted/50">
                                <td className="p-2 font-medium">{pos.ticker}</td>
                                <td className="p-2 text-sm text-muted-foreground max-w-[200px] truncate">{pos.description}</td>
                                <td className="text-right p-2">{pos.quantity.toFixed(2)}</td>
                                <td className="text-right p-2">{formatCurrency(pos.marketValue, pos.currency)}</td>
                                <td className="text-right p-2">{formatCurrency(pos.costBasis, pos.currency)}</td>
                                <td className={`text-right p-2 font-medium ${pos.unrealizedPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatCurrency(pos.unrealizedPL, pos.currency)}
                                </td>
                                <td className={`text-right p-2 font-medium ${pos.unrealizedPLPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatPercent(pos.unrealizedPLPercent)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </TabsContent>

              <TabsContent value="positions" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>All Positions ({analysisData.allPositions.length})</CardTitle>
                    <CardDescription>Click on any row to see detailed trading scorecard with profit targets and stop-loss levels</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2">Alert</th>
                            <th 
                              className="text-left p-2 cursor-pointer hover:bg-muted/50 select-none"
                              onClick={() => handleSort('ticker')}
                            >
                              Ticker <SortIcon field="ticker" />
                            </th>
                            <th 
                              className="text-left p-2 cursor-pointer hover:bg-muted/50 select-none"
                              onClick={() => handleSort('description')}
                            >
                              Description <SortIcon field="description" />
                            </th>
                            <th 
                              className="text-left p-2 cursor-pointer hover:bg-muted/50 select-none"
                              onClick={() => handleSort('sector')}
                            >
                              Sector <SortIcon field="sector" />
                            </th>
                            <th 
                              className="text-right p-2 cursor-pointer hover:bg-muted/50 select-none"
                              onClick={() => handleSort('quantity')}
                            >
                              Quantity <SortIcon field="quantity" />
                            </th>
                            <th className="text-center p-2">Symbol</th>
                            <th 
                              className="text-right p-2 cursor-pointer hover:bg-muted/50 select-none"
                              onClick={() => handleSort('avgCost')}
                            >
                              Avg Cost <SortIcon field="avgCost" />
                            </th>
                            <th 
                              className="text-right p-2 cursor-pointer hover:bg-muted/50 select-none"
                              onClick={() => handleSort('currentPrice')}
                            >
                              Current Price <SortIcon field="currentPrice" />
                            </th>
                            <th 
                              className="text-right p-2 cursor-pointer hover:bg-muted/50 select-none"
                              onClick={() => handleSort('marketValue')}
                            >
                              Market Value <SortIcon field="marketValue" />
                            </th>
                            <th className="text-right p-2">Next Target</th>
                            <th className="text-right p-2">Stop Loss (5%)</th>
                            <th 
                              className="text-right p-2 cursor-pointer hover:bg-muted/50 select-none"
                              onClick={() => handleSort('unrealizedPL')}
                            >
                              Unrealized P/L <SortIcon field="unrealizedPL" />
                            </th>
                            <th 
                              className="text-right p-2 cursor-pointer hover:bg-muted/50 select-none"
                              onClick={() => handleSort('unrealizedPLPercent')}
                            >
                              P/L % <SortIcon field="unrealizedPLPercent" />
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {getSortedPositions(analysisData.allPositions).map((pos, idx) => {
                            const gainPct = pos.unrealizedPLPercent;
                            const alertColor = gainPct >= 45 ? 'bg-red-500/10' :
                                             gainPct >= 30 ? 'bg-orange-500/10' :
                                             gainPct >= 15 ? 'bg-yellow-500/10' :
                                             gainPct < -5 ? 'bg-red-600/10' : '';
                            
                            const profitTargets = [
                              { ratio: '2:1', price: pos.avgCost * 1.10 },
                              { ratio: '3:1', price: pos.avgCost * 1.15 },
                              { ratio: '4:1', price: pos.avgCost * 1.20 },
                              { ratio: '6:1', price: pos.avgCost * 1.30 },
                            ];
                            const nextTarget = profitTargets.find(t => pos.currentPrice < t.price);
                            const stopLoss = pos.avgCost * 0.95;
                            
                            return (
                              <tr 
                                key={idx} 
                                className={cn(
                                  "border-b hover:bg-muted/80 cursor-pointer transition-all",
                                  alertColor
                                )}
                                onClick={() => {
                                  setSelectedPosition(pos);
                                  setScorecardOpen(true);
                                }}
                              >
                                <td className="p-2">
                                  <div className={cn(
                                    "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold whitespace-nowrap",
                                    gainPct >= 45 ? "bg-red-500 text-white animate-pulse" :
                                    gainPct >= 30 ? "bg-orange-500 text-white" :
                                    gainPct >= 15 ? "bg-yellow-500 text-white" :
                                    gainPct < -5 ? "bg-red-600 text-white" :
                                    "bg-gray-400 text-white"
                                  )}>
                                    {gainPct >= 45 ? '🎯 SELL' :
                                     gainPct >= 30 ? '⚡' :
                                     gainPct >= 15 ? '💰' :
                                     gainPct < -5 ? '🛑' : 'HOLD'}
                                  </div>
                                </td>
                                <td className="p-2 font-medium">{pos.ticker}</td>
                                <td className="p-2 text-sm text-muted-foreground max-w-[200px] truncate">{pos.description}</td>
                                <td className="p-2 text-sm">{pos.sector}</td>
                                <td className="text-right p-2">{pos.quantity.toFixed(4)}</td>
                                <td className="text-center p-2 font-medium text-lg">{pos.currency}</td>
                                <td className="text-right p-2">{formatCurrency(pos.avgCost, pos.currency)}</td>
                                <td className="text-right p-2 font-bold">{formatCurrency(pos.currentPrice, pos.currency)}</td>
                                <td className="text-right p-2 font-medium">{formatCurrency(pos.marketValue, pos.currency)}</td>
                                <td className="text-right p-2">
                                  {nextTarget ? (
                                    <div>
                                      <div className="font-bold text-green-600 text-sm">{formatCurrency(nextTarget.price, pos.currency)}</div>
                                      <div className="text-xs text-muted-foreground">{nextTarget.ratio}</div>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-green-600">Target reached ✓</span>
                                  )}
                                </td>
                                <td className="text-right p-2">
                                  <div className="font-bold text-red-600 text-sm">{formatCurrency(stopLoss, pos.currency)}</div>
                                  <div className="text-xs text-muted-foreground">-5%</div>
                                </td>
                                <td className={`text-right p-2 font-medium ${pos.unrealizedPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatCurrency(pos.unrealizedPL, pos.currency)}
                                </td>
                                <td className={`text-right p-2 font-medium ${pos.unrealizedPLPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatPercent(pos.unrealizedPLPercent)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Legend */}
                    <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                      <div className="space-y-3">
                        <div className="text-sm font-semibold">Alert Legend:</div>
                        <div className="flex items-center gap-6 text-sm flex-wrap">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-red-500 rounded"></div>
                            <span className="text-xs">≥45% - Take Profit Now</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-orange-500 rounded"></div>
                            <span className="text-xs">≥30% - Profit Zone</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                            <span className="text-xs">≥15% - Profit Alert</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-red-600 rounded"></div>
                            <span className="text-xs">&lt;-5% - Stop Loss Alert</span>
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground mt-2">
                          <strong>Click any row</strong> to see detailed trading scorecard with profit targets and stop-loss recommendations
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="benchmarks" className="space-y-4">
                {/* Benchmark Summary Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>Performance Summary</CardTitle>
                    <CardDescription>Comparison across different time periods</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2">Index</th>
                            <th className="text-right p-2">MTD</th>
                            <th className="text-right p-2">QTD</th>
                            <th className="text-right p-2">YTD</th>
                            <th className="text-right p-2">1 Year</th>
                            <th className="text-right p-2">Since Inception</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analysisData.benchmarkSummary.map((benchmark, idx) => (
                            <tr key={idx} className={`border-b hover:bg-muted/50 ${benchmark.name === 'Portfolio' ? 'bg-primary/5 font-medium' : ''}`}>
                              <td className="p-2">{benchmark.name}</td>
                              <td className={`text-right p-2 ${benchmark.mtd !== null && benchmark.mtd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {benchmark.mtd !== null ? formatPercent(benchmark.mtd) : '-'}
                              </td>
                              <td className={`text-right p-2 ${benchmark.qtd !== null && benchmark.qtd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {benchmark.qtd !== null ? formatPercent(benchmark.qtd) : '-'}
                              </td>
                              <td className={`text-right p-2 ${benchmark.ytd !== null && benchmark.ytd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {benchmark.ytd !== null ? formatPercent(benchmark.ytd) : '-'}
                              </td>
                              <td className={`text-right p-2 ${benchmark.oneYear !== null && benchmark.oneYear >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {benchmark.oneYear !== null ? formatPercent(benchmark.oneYear) : '-'}
                              </td>
                              <td className={`text-right p-2 ${benchmark.sinceInception !== null && benchmark.sinceInception >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {benchmark.sinceInception !== null ? formatPercent(benchmark.sinceInception) : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Monthly Performance Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle>Monthly Performance Comparison</CardTitle>
                    <CardDescription>Portfolio vs major indices (monthly returns)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart data={analysisData.monthlyComparison.filter(d => d.portfolio !== null)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="period" />
                        <YAxis label={{ value: 'Return %', angle: -90, position: 'insideLeft' }} />
                        <Tooltip formatter={(value) => value !== null ? `${(value as number).toFixed(2)}%` : '-'} />
                        <Legend />
                        <Line type="monotone" dataKey="portfolio" stroke="#8884d8" strokeWidth={3} name="Your Portfolio" connectNulls />
                        <Line type="monotone" dataKey="sp500" stroke="#82ca9d" strokeWidth={2} name="S&P 500 (SPXTR)" connectNulls />
                        <Line type="monotone" dataKey="efa" stroke="#ffc658" strokeWidth={2} name="EFA" connectNulls />
                        <Line type="monotone" dataKey="vt" stroke="#ff7c7c" strokeWidth={2} name="VT" connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Quarterly Performance Chart */}
                {analysisData.quarterlyComparison.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Quarterly Performance Comparison</CardTitle>
                      <CardDescription>Portfolio vs major indices (quarterly returns)</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={analysisData.quarterlyComparison.filter(d => d.portfolio !== null)}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="period" />
                          <YAxis label={{ value: 'Return %', angle: -90, position: 'insideLeft' }} />
                          <Tooltip formatter={(value) => value !== null ? `${(value as number).toFixed(2)}%` : '-'} />
                          <Legend />
                          <Bar dataKey="portfolio" fill="#8884d8" name="Your Portfolio" />
                          <Bar dataKey="sp500" fill="#82ca9d" name="S&P 500 (SPXTR)" />
                          <Bar dataKey="efa" fill="#ffc658" name="EFA" />
                          <Bar dataKey="vt" fill="#ff7c7c" name="VT" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>

            {/* Stock Scorecards - Action Summary */}
            <div className="mt-12 space-y-6">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Target className="h-6 w-6 text-primary" />
                Action Scorecards - Quick Decision Guide
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
                {/* Take Profit Now */}
                {(() => {
                  const takeProfitPositions = analysisData.allPositions.filter(p => p.unrealizedPLPercent >= 45);
                  return takeProfitPositions.length > 0 && (
                    <Card className="border-2 border-red-500 bg-red-500/5">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <div className="h-3 w-3 bg-red-500 rounded-full animate-pulse"></div>
                          Take Profit Now ({takeProfitPositions.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {takeProfitPositions.map((pos) => (
                          <div 
                            key={pos.ticker} 
                            className="p-3 bg-background rounded-lg border border-red-200 cursor-pointer hover:bg-red-50 transition-colors"
                            onClick={() => {
                              setSelectedPosition(pos);
                              setScorecardOpen(true);
                            }}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-bold">{pos.ticker}</span>
                              <Badge className="bg-red-500 text-white">+{pos.unrealizedPLPercent.toFixed(2)}%</Badge>
                            </div>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Sell 50%:</span>
                                <span className="font-semibold">{Math.floor(pos.quantity * 0.5)} shares</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Lock in:</span>
                                <span className="font-semibold text-green-600">
                                  {formatCurrency(Math.floor(pos.quantity * 0.5) * pos.currentPrice, pos.currency)}
                                </span>
                              </div>
                            </div>
                            <div className="mt-2 pt-2 border-t">
                              <p className="text-xs font-medium text-red-600">🎯 Click for full scorecard</p>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  );
                })()}

                {/* Profit Zone */}
                {(() => {
                  const profitZonePositions = analysisData.allPositions.filter(p => p.unrealizedPLPercent >= 30 && p.unrealizedPLPercent < 45);
                  return profitZonePositions.length > 0 && (
                    <Card className="border-2 border-orange-500 bg-orange-500/5">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5 text-orange-500" />
                          Profit Zone ({profitZonePositions.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {profitZonePositions.map((pos) => (
                          <div 
                            key={pos.ticker} 
                            className="p-3 bg-background rounded-lg border border-orange-200 cursor-pointer hover:bg-orange-50 transition-colors"
                            onClick={() => {
                              setSelectedPosition(pos);
                              setScorecardOpen(true);
                            }}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-bold">{pos.ticker}</span>
                              <Badge className="bg-orange-500 text-white">+{pos.unrealizedPLPercent.toFixed(2)}%</Badge>
                            </div>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Current:</span>
                                <span className="font-semibold">{formatCurrency(pos.currentPrice, pos.currency)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Stop Loss:</span>
                                <span className="font-semibold text-red-600">{formatCurrency(pos.avgCost * 0.95, pos.currency)}</span>
                              </div>
                            </div>
                            <div className="mt-2 pt-2 border-t">
                              <p className="text-xs font-medium text-orange-600">⚡ Click for full scorecard</p>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  );
                })()}

                {/* Profit Alert */}
                {(() => {
                  const profitAlertPositions = analysisData.allPositions.filter(p => p.unrealizedPLPercent >= 15 && p.unrealizedPLPercent < 30);
                  return profitAlertPositions.length > 0 && (
                    <Card className="border-2 border-yellow-500 bg-yellow-500/5">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <TrendingUp className="h-5 w-5 text-yellow-600" />
                          Profit Alert ({profitAlertPositions.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {profitAlertPositions.map((pos) => (
                          <div 
                            key={pos.ticker} 
                            className="p-3 bg-background rounded-lg border border-yellow-200 cursor-pointer hover:bg-yellow-50 transition-colors"
                            onClick={() => {
                              setSelectedPosition(pos);
                              setScorecardOpen(true);
                            }}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-bold">{pos.ticker}</span>
                              <Badge className="bg-yellow-500 text-white">+{pos.unrealizedPLPercent.toFixed(2)}%</Badge>
                            </div>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Target:</span>
                                <span className="font-semibold text-green-600">{formatCurrency(pos.avgCost * 1.15, pos.currency)}</span>
                              </div>
                            </div>
                            <div className="mt-2 pt-2 border-t">
                              <p className="text-xs font-medium text-yellow-700">💰 Click for full scorecard</p>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  );
                })()}

                {/* Stop Loss Alert */}
                {(() => {
                  const stopLossPositions = analysisData.allPositions.filter(p => p.unrealizedPLPercent < -5);
                  return stopLossPositions.length > 0 && (
                    <Card className="border-2 border-red-600 bg-red-600/5">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <ShieldAlert className="h-5 w-5 text-red-600" />
                          Stop Loss Alert ({stopLossPositions.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {stopLossPositions.map((pos) => (
                          <div 
                            key={pos.ticker} 
                            className="p-3 bg-background rounded-lg border border-red-300 cursor-pointer hover:bg-red-50 transition-colors"
                            onClick={() => {
                              setSelectedPosition(pos);
                              setScorecardOpen(true);
                            }}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-bold">{pos.ticker}</span>
                              <Badge variant="destructive">{pos.unrealizedPLPercent.toFixed(2)}%</Badge>
                            </div>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Loss:</span>
                                <span className="font-semibold text-red-600">{formatCurrency(Math.abs(pos.unrealizedPL), pos.currency)}</span>
                              </div>
                            </div>
                            <div className="mt-2 pt-2 border-t">
                              <p className="text-xs font-medium text-red-600">🛑 Click for full scorecard</p>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  );
                })()}
              </div>

              {/* Summary Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Portfolio Alert Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-red-500/10 rounded-lg">
                      <div className="text-3xl font-bold text-red-600">
                        {analysisData.allPositions.filter(p => p.unrealizedPLPercent >= 45).length}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">Take Profit Now</div>
                    </div>
                    <div className="text-center p-4 bg-orange-500/10 rounded-lg">
                      <div className="text-3xl font-bold text-orange-600">
                        {analysisData.allPositions.filter(p => p.unrealizedPLPercent >= 30 && p.unrealizedPLPercent < 45).length}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">In Profit Zone</div>
                    </div>
                    <div className="text-center p-4 bg-yellow-500/10 rounded-lg">
                      <div className="text-3xl font-bold text-yellow-600">
                        {analysisData.allPositions.filter(p => p.unrealizedPLPercent >= 15 && p.unrealizedPLPercent < 30).length}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">Profit Alerts</div>
                    </div>
                    <div className="text-center p-4 bg-red-600/10 rounded-lg">
                      <div className="text-3xl font-bold text-red-700">
                        {analysisData.allPositions.filter(p => p.unrealizedPLPercent < -5).length}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">Stop Loss Alerts</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Individual Stock Scorecard Dialog */}
        <IndividualStockScorecard 
          position={selectedPosition}
          open={scorecardOpen}
          onClose={() => setScorecardOpen(false)}
        />

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              How to Export CSV from IBKR
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <h3 className="font-medium">Step 1: Access Reports</h3>
              <p className="text-sm text-muted-foreground">
                Log in to your IBKR account → Performance & Reports → Statements
              </p>
            </div>
            <div className="space-y-2">
              <h3 className="font-medium">Step 2: Select Period & Format</h3>
              <p className="text-sm text-muted-foreground">
                Choose "Custom" or "Year to Date", select date range, and pick "CSV" format
              </p>
            </div>
            <div className="space-y-2">
              <h3 className="font-medium">Step 3: Download & Upload</h3>
              <p className="text-sm text-muted-foreground">
                Download the CSV file and upload it here for comprehensive analysis
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default IBKRImport;
